import { KeyboardEvent, useState, useEffect } from "react";
import {
  Flex,
  Switch,
  Text,
  Icon,
  FormControl,
} from "@chakra-ui/react";
import { maskJs } from "mask-js";
import { AiOutlineInfoCircle } from "react-icons/ai";
import {
  cellPhone,
  cep,
  cnpj,
  cpf,
  cpfCnpjMask,
  currency,
  formatCurrency,
  onlyLetters,
  percentage,
  percentageRacional,
  phone,
  simpleDate,
  fullDate,
  onlyNumbers,
  formatBarcode,
} from "@/utils";
import { Tooltip, Input as InputBarte } from "barte-design-system";

export interface InputProps {
  type?: string;
  name: string;
  placeholder?: string;
  switchActive?: boolean;
  periodicityName?: string;
  containerDisabled?: boolean;
  hasMonthlyValue?: boolean;
  valueData?: any;
  asSubscription?: boolean;
  tooltipText?: string;
  hasSwitch?: boolean;
  label?: string;
  labelColor?: string;
  register?: any;
  setValue?: any;
  errors?: any;
  mask?: string;
  valueLenght?: number;
  classInput?: string;
  before?: string;
  after?: string;
  variant?: string;
  switchFunction?: (any?: any) => void;
  color?: string;
  asDoubleEffect?: boolean;
  switchDisabled?: boolean;
  optionalText?: string;
  afterFontSize?: string;
  afterColor?: string;
  afterBackgroundColor?: string;
  noMarginOnError?: boolean;
  icon?: React.ReactNode;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  visibility?: string;
  className?: string;
  compact?: boolean;
  [key: string]: any;
}

export function Input({
  type = "text",
  name,
  setValue,
  placeholder,
  switchActive,
  containerDisabled = true,
  periodicityName,
  valueLenght,
  hasMonthlyValue,
  valueData,
  asSubscription,
  onChange,
  tooltipText,
  label,
  labelColor = "#000000",
  register,
  errors,
  mask,
  hasSwitch = false,
  classInput,
  before,
  after,
  variant,
  switchFunction,
  color,
  asDoubleEffect,
  switchDisabled,
  optionalText,
  afterFontSize = "2xl",
  afterColor = "gray.400",
  afterBackgroundColor = "gray.100",
  noMarginOnError,
  icon,
  compact,
  ...rest
}: InputProps) {
  const [disabledState, setDisabledState] = useState(
    hasSwitch || containerDisabled
  );

  const handleMaskInput = (e: KeyboardEvent<HTMLInputElement>) => {
    if (
      mask === "cpf/cnpj" ||
      mask === "currency" ||
      mask === "currency(noBrSign)" ||
      mask === "percentage" ||
      mask === "percentageRacional"
    )
      return;
    if (mask === "cep") {
      cep(e);
    } else if (mask === "phone") {
      phone(e);
    } else if (mask === "cpf") {
      cpf(e);
    } else if (mask === "cellphone") {
      cellPhone(e);
    } else if (mask === "cnpj") {
      cnpj(e);
    } else if (mask === "onlyLetters") {
      onlyLetters(e);
    } else if (mask === "onlyNumbers") {
      onlyNumbers(e);
    }
    else if (mask === "simpleDate") {
      simpleDate(e)
    }
    else if (mask === "fullDate") {
      fullDate(e)
    }
    else if (mask === "bankSlip") {
      formatBarcode(e)
    }
    else if (mask) {
      e.currentTarget.value = maskJs(mask, e.currentTarget.value);
    }
  };

  const handleMaskInputCurrency = (e: KeyboardEvent<HTMLInputElement>) => {
    //@ts-ignore
    const isNumber = isFinite(e.key);
    if (mask === "currency" && isNumber) {
      currency(e)

    }
    if (mask === "currency(noBrSign)" && isNumber) {
      currency(e, true);
      return;
    }
    if (mask === "percentageRacional") {
      percentageRacional(e);
      return;
    }
  };

  const handleMaskOnBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // Converte para KeyboardEvent para compatibilidade com as funções de máscara
    const keyboardEvent = e as unknown as KeyboardEvent<HTMLInputElement>;
    
    if (mask === "cep") {
      cep(keyboardEvent);
    } else if (mask === "cpf/cnpj") {
      cpfCnpjMask(keyboardEvent);
    } else if (mask === "percentage") {
      percentage(keyboardEvent);
    } else if (mask === "percentageRacional") {
      percentageRacional(keyboardEvent, true);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // Primeiro aplica a máscara se necessário
    handleMaskOnBlur(e);
    
    // Depois chama o onBlur do register se houver
    if (register?.onBlur) {
      register.onBlur(e);
    }
    
    // Por último chama o onBlur customizado passado via rest se houver
    if (rest?.onBlur) {
      rest.onBlur(e);
    }
  };


  function handleSwitchChange(e: any) {
    if (asDoubleEffect) {
      switchFunction(e);
    }

    if(disabledState === false){
      setValue ? setValue(name, null) : null;

    }
    setDisabledState(!disabledState);
    setValue ? setValue(name, null) : null;
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Chama register.onChange primeiro para garantir que o react-hook-form seja atualizado
    if (register?.onChange) {
      register.onChange(e);
    }
    // Depois chama o onChange customizado se houver
    if (onChange) {
      onChange(e);
    }
  };

  const isDisabled = (hasSwitch && disabledState) || !containerDisabled || (asDoubleEffect && !switchActive);
  const inputType = type === "textarea" ? "textarea" : (type === "password" ? "password" : "text");
  
  // Props que o InputBarte aceita
  const inputBarteProps: any = {
    type: inputType,
    label: label && (rest as any)?.visibility !== "hidden" ? label : undefined,
    labelOptional: optionalText,
    helpText: tooltipText,
    placeholder: placeholder,
    error: errors?.message,
    icon: icon,
    className: classInput || (rest as any)?.className,
    disabled: isDisabled,
  };

  // Extrai as propriedades do register, mas mantém nosso onChange customizado
  const { onChange: registerOnChange, onBlur: registerOnBlur, ref, ...registerRest } = register || {};
  
  const inputEvents = {
    onBlurCapture: handleMaskOnBlur,
    onKeyUp: handleMaskInput,
    onKeyDown: handleMaskInputCurrency,
    name,
    onChange: handleChange,
    ...(register ? { ref, onBlur: registerOnBlur, ...registerRest } : {}),
    ...(!register && rest?.value !== undefined ? { value: rest.value } : {}),
    ...(!register && rest?.onBlur ? { onBlur: rest.onBlur } : {}),
    ...(rest?.readOnly !== undefined ? { readOnly: rest.readOnly } : {}),
  };

  useEffect(() => {
    setDisabledState(!switchActive);
  }, []);

  if (compact) {
    return (
      <InputBarte
        {...inputBarteProps}
        {...inputEvents}
        {...(rest?.style ? { style: rest.style } : {})}
      />
    );
  }

  return (
    <FormControl isInvalid={!!errors?.message}>
      <Flex flexDir="column">
        {(hasSwitch || tooltipText) && (
          <Flex align="center" w="332px" justify="space-between" mb=".2rem">
            {tooltipText && (
              <Tooltip placement="top" label={tooltipText}>
                <span>
                  <Icon w=".8rem" color="gray.400" as={AiOutlineInfoCircle} />
                </span>
              </Tooltip>
            )}
            {hasSwitch && !asDoubleEffect && (
              <Switch
                name={name}
                defaultChecked={switchActive}
                onChange={handleSwitchChange}
                size="sm"
                colorScheme="cyan"
                isDisabled={switchDisabled}
              />
            )}

            {hasSwitch && asDoubleEffect && (
              <Switch
                name={name}
                isChecked={switchActive}
                defaultChecked={switchActive}
                onChange={handleSwitchChange}
                size="sm"
                colorScheme="cyan"
                isDisabled={switchDisabled}
              />
            )}
          </Flex>
        )}
        <Flex flexDir="column" position="relative">
          <InputBarte
            {...inputBarteProps}
            {...inputEvents}
            {...(rest?.style ? { style: rest.style } : {})}
          />
          {asSubscription && hasMonthlyValue && (
            <Text
              fontSize="12px"
              color="newGray.300"
              position="absolute"
              top="3"
              right="2"
            >
              {formatCurrency({ value: valueData?.valuePerMonth })}/mês
            </Text>
          )}
        </Flex>
        {asSubscription && (
          <>
            <Flex color="newGray.300" justify="space-between">
              <Text fontSize="10px">
                Valor original:{" "}
                {formatCurrency({ value: valueData?.originalValue })}/
                {periodicityName}
              </Text>
              {hasMonthlyValue && (
                <Text fontSize="10px">
                  {formatCurrency({ value: valueData?.originalValuePerMonth })}
                  /mês
                </Text>
              )}
            </Flex>
          </>
        )}
      </Flex>
    </FormControl>
  );
}
